import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { toast } from "sonner";
import { Navigation } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { PageHeader, Field, TextInput, ActionButton, EmptyState } from "../../components/common";
import { MapCenterControl } from "../../components/MapCenterControl";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

const PERMIAN = [31.9973, -102.0779];

export default function SwdMap() {
  const [locations, setLocations] = useState([]);
  const [manual, setManual] = useState({ lat: "", lng: "", label: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/swd/locations").then((r) => setLocations(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const gpsCheckIn = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser — use manual entry");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/swd/locations", { lat: pos.coords.latitude, lng: pos.coords.longitude, label: "GPS check-in" });
          toast.success("GPS check-in logged");
          load();
        } catch (e) {
          toast.error(errMsg(e));
        } finally {
          setBusy(false);
        }
      },
      () => {
        toast.error("Location permission denied — use manual entry below");
        setBusy(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const manualCheckIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/swd/locations", {
        lat: Number(manual.lat),
        lng: Number(manual.lng),
        ...(manual.label ? { label: manual.label } : {}),
      });
      toast.success("Location logged");
      setManual({ lat: "", lng: "", label: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="swd-map-page">
      <PageHeader title="GPS Check-ins" subtitle="Driver locations captured from the field — latest pings on the map" testId="swd-map-header">
        <ActionButton testId="gps-checkin-trigger-button" onClick={gpsCheckIn} disabled={busy}>
          <Navigation size={16} /> {busy ? "Locating…" : "GPS Check-in"}
        </ActionButton>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-slate-700/60 rounded-lg overflow-hidden" data-testid="gps-map-container">
          <MapContainer center={PERMIAN} zoom={8} style={{ height: "480px", width: "100%" }}>
            <MapCenterControl pins={locations} testId="swd-map" />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locations.map((loc) => (
              <Marker key={loc.id} position={[loc.lat, loc.lng]} data-testid={`map-marker-${loc.id}`}>
                <Popup>
                  <div className="text-xs">
                    <p className="font-semibold">{loc.driver_name}</p>
                    <p>{loc.label || "Check-in"}</p>
                    <p className="text-slate-400">{fmtDate(loc.captured_at)}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="space-y-6">
          <form onSubmit={manualCheckIn} className="bg-card border border-slate-700/60 rounded-lg p-5 space-y-4" data-testid="manual-checkin-form">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Manual Entry</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude">
                <TextInput testId="manual-lat-input" type="number" step="any" value={manual.lat} onChange={(e) => setManual({ ...manual, lat: e.target.value })} required placeholder="31.9973" />
              </Field>
              <Field label="Longitude">
                <TextInput testId="manual-lng-input" type="number" step="any" value={manual.lng} onChange={(e) => setManual({ ...manual, lng: e.target.value })} required placeholder="-102.0779" />
              </Field>
            </div>
            <Field label="Label">
              <TextInput testId="manual-label-input" value={manual.label} onChange={(e) => setManual({ ...manual, label: e.target.value })} placeholder="SWD facility gate" />
            </Field>
            <ActionButton testId="manual-checkin-button" type="submit" disabled={busy} className="w-full justify-center">
              Log Location
            </ActionButton>
          </form>

          <div className="bg-card border border-slate-700/60 rounded-lg p-5" data-testid="recent-checkins">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-3">Recent Check-ins</p>
            {locations.length === 0 ? (
              <EmptyState message="No check-ins yet" testId="checkins-empty" />
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {locations.slice(0, 15).map((loc) => (
                  <div key={loc.id} className="flex justify-between items-start border-b border-slate-800 pb-2" data-testid={`checkin-row-${loc.id}`}>
                    <div>
                      <p className="text-sm text-slate-200">{loc.driver_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}{loc.label ? ` · ${loc.label}` : ""}</p>
                    </div>
                    <span className="text-[10px] font-mono text-slate-500">{fmtDate(loc.captured_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
